import { useEffect } from 'react';

import { cn } from '@april9au/react-common/utils';

import type { ActivityCardSlice } from '@/chat/activity';
import type { AnswerPayload, Prompt } from '@/chat/types';
import { activityReceipt, isDraftComplete, markDraft } from '@/chat/activity';
import { ACTIVITY_REVEAL_AFTER, pastWrongs } from '@/chat/reducer';
import { Icon } from '@/components/ui/icon';
import { useStrings } from '@/i18n/use-strings';
import { useActivityDraft } from './draft';

/**
 * The footer while an activity card is armed: what to do, then Reset and
 * Check. This is where an activity is marked — the card draws, the footer
 * arms it, reads the draft back and reports the verdict to the reducer.
 *
 * After three missed checks, a second row offers "Show me the answer". The
 * app never reveals an answer unasked.
 *
 * `data-autopilot` marks the controls for the non-production Autopilot, which
 * presses them for real.
 */

export type ActivityFooterProps = {
  prompt: Extract<Prompt, { kind: 'activity' }>;
  attempt: number;
  onAnswer: (payload: AnswerPayload) => void;
  /** The `reveal` event, which shows this card's answer. */
  onReveal: () => void;
};

export function ActivityFooter({ prompt, attempt, onAnswer, onReveal }: ActivityFooterProps) {
  const { chat } = useStrings();
  const arm = useActivityDraft((state) => state.arm);
  const reset = useActivityDraft((state) => state.reset);
  const commit = useActivityDraft((state) => state.commit);
  const active = useActivityDraft((state) => state.active);
  // Free text answers in the footer itself, so it never reaches this prompt.
  const slice = prompt.slice.variation === 'free_text' ? null : (prompt.slice as ActivityCardSlice);

  useEffect(() => {
    if (slice) {
      arm(prompt.stepId, attempt, slice);
    }
  }, [arm, prompt.stepId, attempt, slice]);

  if (!slice) {
    return null;
  }

  const draft = active?.stepId === prompt.stepId ? active.draft : null;
  const ready = draft !== null && isDraftComplete(slice, draft);
  const offerReveal = pastWrongs(attempt, ACTIVITY_REVEAL_AFTER);

  const check = () => {
    if (draft === null) {
      return;
    }

    const verdict = markDraft(slice, draft);

    commit();
    onAnswer({
      kind: 'activity',
      correct: verdict.correct,
      // The learner's own words back: labels, never coordinates.
      receipt: activityReceipt(slice.primary.receipt, verdict.labels, chat.chat_activity_elsewhere),
      ...(verdict.mistake === null ? {} : { mistake: verdict.mistake }),
    });
  };

  const showAnswer = () => {
    commit();
    onReveal();
  };

  return (
    /*
      The first row fits the footer's minimum height, so arming an activity
      does not resize the footer and shift the thread. Only the reveal row
      grows it, and that arrives on a re-arm while the thread is moving anyway.
    */
    <div data-autopilot="answer" data-autopilot-kind="activity" className="flex flex-col gap-2">
      <div className="flex gap-2">
        <button
          type="button"
          data-autopilot="reset"
          onClick={() => reset(slice)}
          className={cn(
            'chat-pressable border-chat-hairline bg-bubble-buddy text-chat-ink flex min-h-11 items-center gap-2',
            'rounded-xl border px-4 text-[15px] font-semibold',
            'hover:border-surf-blue focus-visible:ring-surf-blue/40 focus-visible:ring-[3px] focus-visible:outline-none',
          )}
        >
          <Icon name="passport/reset" className="size-4" />
          {chat.chat_reset}
        </button>
        <button
          type="button"
          data-autopilot="check"
          disabled={!ready}
          onClick={check}
          className={cn(
            'chat-pressable border-surf-red bg-surf-red flex min-h-11 flex-1 items-center justify-center',
            'rounded-xl border px-4 text-[15px] font-semibold text-white',
            'focus-visible:ring-surf-red/40 focus-visible:ring-[3px] focus-visible:outline-none',
            'disabled:border-chat-hairline disabled:bg-chat-rail disabled:text-chat-ink-muted',
          )}
        >
          {chat.chat_check_answer}
        </button>
      </div>

      {offerReveal ? (
        <button
          type="button"
          data-autopilot="reveal"
          onClick={showAnswer}
          className={cn(
            'chat-pressable text-surf-blue flex min-h-10 w-full items-center justify-center gap-1.5 rounded-xl',
            'text-[14px] font-semibold underline-offset-4 hover:underline',
            'focus-visible:ring-surf-blue/40 focus-visible:ring-[3px] focus-visible:outline-none',
          )}
        >
          {chat.chat_show_answer}
          <Icon name="passport/chevron-right" aria-hidden className="size-4 rtl:rotate-180" />
        </button>
      ) : null}
    </div>
  );
}
